import React from 'react';

import { STATES, STATE_NAMES, TIMEZONES, formatNumber, zoneAbbreviation } from './format';

const LISTINGS_PER_PAGE = 50;

/** The only tenancies iCLIMB can turn into a TYPE. Crexi also sells Vacant. */
const TENANCIES = ['Single', 'Multi'];

const BLANK_SEARCH = {
    name: '',
    types: [],
    subtypes: [],
    tenancies: [],
    asking_price_min: null,
    asking_price_max: null,
    sqft_min: null,
};

// What the validation banner calls each field the server rejected.
const FIELD_LABELS = {
    enabled: 'Nightly run',
    run_at: 'Start at',
    timezone: 'Timezone',
    max_pages: 'Page ceiling',
    cutoff_days: 'Cut-off',
    states: 'States',
    searches: 'Saved searches',
};

/**
 * Everything an operator can change without a deploy.
 *
 * The saved searches are the consequential part: they are what bounds the
 * crawl, and a listing is written only because one of them asked for it. The
 * rest — when it runs, how deep, which states — is the frame around them.
 */
export default class SettingsModal extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            draft: Object.assign({}, props.settings),
            stateOpen: false,
            stateQuery: '',
            subtypeText: {},
            zoneOpen: false,
            saving: false,
            saved: false,
            errors: null,
            failed: null,
        };

        this.onKeyDown = this.onKeyDown.bind(this);
        this.save = this.save.bind(this);
        this.discard = this.discard.bind(this);
    }

    componentDidMount() {
        document.addEventListener('keydown', this.onKeyDown);
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.onKeyDown);
    }

    onKeyDown(event) {
        if (event.key !== 'Escape') return;

        // Escape closes the menu it opened before it closes the dialog —
        // otherwise dismissing a dropdown throws away an unsaved form.
        if (this.state.stateOpen || this.state.zoneOpen) {
            this.setState({ stateOpen: false, zoneOpen: false });
            return;
        }

        this.props.onClose();
    }

    set(field, value) {
        this.setState({
            draft: Object.assign({}, this.state.draft, { [field]: value }),
            saved: false,
        });
    }

    dirty() {
        const { draft } = this.state;
        return Object.keys(draft).some((key) => JSON.stringify(draft[key]) !== JSON.stringify(this.props.settings[key]));
    }

    searches() {
        return this.state.draft.searches || [];
    }

    setSearch(index, changes) {
        this.set('searches', this.searches().map((search, position) => (
            position === index ? Object.assign({}, search, changes) : search
        )));
    }

    toggleInSearch(index, field, value) {
        const chosen = this.searches()[index][field] || [];
        const on = chosen.indexOf(value) !== -1;

        this.setSearch(index, {
            [field]: on ? chosen.filter((item) => item !== value) : chosen.concat([value]),
        });
    }

    addSearch() {
        this.set('searches', this.searches().concat([Object.assign({}, BLANK_SEARCH)]));
    }

    removeSearch(index) {
        this.set('searches', this.searches().filter((search, position) => position !== index));
        this.setState({ subtypeText: {} });
    }

    /**
     * Kept as typed rather than as parsed, so that a half-written "Bank, " does
     * not lose its comma the moment the array behind it is rebuilt.
     */
    subtypeText(index) {
        const raw = this.state.subtypeText[index];
        return raw === undefined ? (this.searches()[index].subtypes || []).join(', ') : raw;
    }

    setSubtypes(index, raw) {
        this.setState({ subtypeText: Object.assign({}, this.state.subtypeText, { [index]: raw }) });

        this.setSearch(index, {
            subtypes: raw.split(',').map((item) => item.trim()).filter(Boolean),
        });
    }

    addState(code) {
        const states = this.state.draft.states || [];
        if (states.indexOf(code) === -1) this.set('states', states.concat([code]));
        this.setState({ stateQuery: '' });
    }

    removeState(code) {
        this.set('states', (this.state.draft.states || []).filter((item) => item !== code));
    }

    /**
     * Blank means no limit, and the server draws that distinction from null.
     *
     * Anything that will not parse is sent as typed rather than as NaN, which
     * JSON turns into null — a price floor the operator wrote would silently
     * become "no limit" and the crawl would quietly widen. The server's integer
     * rule refuses the string and says so.
     */
    numberOrNull(value) {
        const trimmed = String(value === null || value === undefined ? '' : value).trim();

        if (trimmed === '') return null;

        // "1,000,000" is how a person writes it.
        const parsed = Number(trimmed.replace(/,/g, ''));

        return Number.isFinite(parsed) ? parsed : trimmed;
    }

    discard() {
        this.setState({
            draft: Object.assign({}, this.props.settings),
            subtypeText: {},
            errors: null,
            failed: null,
            saved: false,
        });
    }

    save() {
        const { draft } = this.state;

        const payload = Object.assign({}, draft, {
            max_pages: Number(draft.max_pages) || 1,

            // Sent as typed rather than rounded up to something workable: a
            // cut-off nobody chose is a crawl reading a window nobody chose.
            cutoff_days: Number(draft.cutoff_days),
            searches: (draft.searches || []).map((search) => Object.assign({}, search, {
                subtypes: search.subtypes || [],
                tenancies: search.tenancies || [],
                asking_price_min: this.numberOrNull(search.asking_price_min),
                asking_price_max: this.numberOrNull(search.asking_price_max),
                sqft_min: this.numberOrNull(search.sqft_min),
            })),
        });

        this.setState({ saving: true, errors: null, failed: null });

        this.props.onSave(payload).then(
            (data) => this.setState({
                saving: false,
                saved: true,
                draft: Object.assign({}, data.settings),
                subtypeText: {},
            }),
            (error) => this.setState({
                saving: false,
                errors: error.errors || null,
                failed: error.message || 'The settings could not be saved.',
            })
        );
    }

    errorFor(field) {
        const errors = this.state.errors;
        if (!errors) return null;
        const key = Object.keys(errors).filter((name) => name === field || name.indexOf(field + '.') === 0)[0];
        return key ? errors[key][0] : null;
    }

    errorHeadline() {
        const { errors, failed } = this.state;

        if (errors) {
            const named = Object.keys(FIELD_LABELS)
                .filter((field) => this.errorFor(field))
                .map((field) => FIELD_LABELS[field]);

            if (named.length) return named.join(' · ') + ' — not saved';
        }

        return failed;
    }

    zoneLabel(zone) {
        const entry = TIMEZONES.filter((candidate) => candidate[0] === zone)[0];
        return entry ? entry[1] + ' — ' + entry[0] : zone;
    }

    renderZonePicker() {
        const { draft, zoneOpen } = this.state;

        return (
            <div className="ls-field ls-field--menu">
                <span className="ls-field__label">Timezone</span>
                <button
                    type="button"
                    className={'ls-select' + (zoneOpen ? ' is-open' : '')}
                    aria-expanded={zoneOpen}
                    onClick={() => this.setState({ zoneOpen: !zoneOpen, stateOpen: false })}
                >
                    <span className="ls-select__value">{this.zoneLabel(draft.timezone)}</span>
                    <svg className={'ls-select__caret' + (zoneOpen ? ' is-open' : '')} width="11" height="11"
                        viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                        <path d="M3 4.3 L5.5 6.8 L8 4.3" />
                    </svg>
                </button>

                {zoneOpen && (
                    <div className="ls-menu">
                        {TIMEZONES.map(([zone, name]) => {
                            const on = draft.timezone === zone;
                            return (
                                <button
                                    key={zone}
                                    type="button"
                                    className={'ls-menu__item' + (on ? ' is-on' : '')}
                                    onClick={() => {
                                        this.set('timezone', zone);
                                        this.setState({ zoneOpen: false });
                                    }}
                                >
                                    <span className="ls-mono ls-menu__abbr">{zoneAbbreviation(zone)}</span>
                                    <span className="ls-menu__name">{name} — {zone}</span>
                                    {on && (
                                        <svg className="ls-menu__check" width="12" height="12" viewBox="0 0 12 12"
                                            fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
                                            <path d="M2.4 6.3 L4.8 8.7 L9.6 3.5" />
                                        </svg>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }

    renderStates() {
        const { draft, stateOpen, stateQuery } = this.state;
        const chosen = draft.states || [];
        const query = stateQuery.trim().toLowerCase();

        const options = STATES
            .filter(([code]) => chosen.indexOf(code) === -1)
            .filter(([code, name]) => !query || code.toLowerCase().indexOf(query) === 0 || name.toLowerCase().indexOf(query) !== -1);

        return (
            <div className="ls-group">
                <div className="ls-group__head">
                    <span className="ls-group__title">States</span>
                    <span className="ls-group__meta">{chosen.length} selected</span>
                </div>

                <div className="ls-chips">
                    {chosen.map((code) => (
                        <span key={code} className="ls-chip" title={STATE_NAMES[code] || code}>
                            {code}
                            <button type="button" className="ls-chip__remove" aria-label={'Remove ' + code}
                                onClick={() => this.removeState(code)}>
                                ×
                            </button>
                        </span>
                    ))}
                    <button
                        type="button"
                        className={'ls-addstate' + (stateOpen ? ' is-open' : '')}
                        aria-expanded={stateOpen}
                        onClick={() => this.setState({ stateOpen: !stateOpen, zoneOpen: false, stateQuery: '' })}
                    >
                        {stateOpen ? 'Done' : 'Add state'}
                        <svg className={'ls-select__caret' + (stateOpen ? ' is-open' : '')} width="11" height="11"
                            viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                            <path d="M3 4.3 L5.5 6.8 L8 4.3" />
                        </svg>
                    </button>
                </div>

                {stateOpen && (
                    <div className="ls-statepicker">
                        <input
                            type="text"
                            className="ls-input"
                            value={stateQuery}
                            autoFocus
                            placeholder="Search states"
                            onChange={(event) => this.setState({ stateQuery: event.target.value })}
                        />
                        <div className="ls-statepicker__grid">
                            {options.map(([code, name]) => (
                                <button key={code} type="button" className="ls-statepicker__option"
                                    onClick={() => this.addState(code)}>
                                    <span className="ls-mono ls-statepicker__code">{code}</span>
                                    <span className="ls-statepicker__name">{name}</span>
                                </button>
                            ))}
                            {options.length === 0 && <div className="ls-statepicker__empty">No state matches</div>}
                        </div>
                    </div>
                )}

                {this.errorFor('states') && <div className="ls-error">{this.errorFor('states')}</div>}
            </div>
        );
    }

    /**
     * One card per saved search, because each carries its own asset types,
     * tenancy and floors — the client's four differ on every one of them.
     */
    renderSearches() {
        const available = this.props.source.available_types || [];
        const searches = this.searches();

        return (
            <div className="ls-group">
                <div className="ls-group__head">
                    <span className="ls-group__title">Saved searches</span>
                    <span className="ls-group__meta">{searches.length} · each run state by state</span>
                </div>

                <div className="ls-savedsearches">
                {searches.map((search, index) => (
                    <div key={index} className="ls-savedsearch">
                        <div className="ls-savedsearch__head">
                            <input
                                type="text"
                                className="ls-input ls-savedsearch__name"
                                placeholder="Name this search"
                                value={search.name || ''}
                                onChange={(event) => this.setSearch(index, { name: event.target.value })}
                            />
                            <button
                                type="button"
                                className="ls-savedsearch__remove"
                                aria-label={'Remove ' + (search.name || 'search')}
                                onClick={() => this.removeSearch(index)}
                            >
                                ×
                            </button>
                        </div>

                        <span className="ls-field__label">Asset types</span>
                        <div className="ls-chips">
                            {available.map((type) => (
                                <button
                                    key={type}
                                    type="button"
                                    className={'ls-toggle' + ((search.types || []).indexOf(type) !== -1 ? ' is-on' : '')}
                                    onClick={() => this.toggleInSearch(index, 'types', type)}
                                >
                                    {type}
                                </button>
                            ))}
                        </div>

                        <span className="ls-field__label">Tenancy</span>
                        <div className="ls-chips">
                            {TENANCIES.map((tenancy) => (
                                <button
                                    key={tenancy}
                                    type="button"
                                    className={'ls-toggle' + ((search.tenancies || []).indexOf(tenancy) !== -1 ? ' is-on' : '')}
                                    onClick={() => this.toggleInSearch(index, 'tenancies', tenancy)}
                                >
                                    {tenancy}
                                </button>
                            ))}
                        </div>

                        <label className="ls-field ls-field--spaced">
                            <span className="ls-field__label">Subtypes</span>
                            <input
                                type="text"
                                className="ls-input"
                                placeholder="Every subtype of the types above"
                                value={this.subtypeText(index)}
                                onChange={(event) => this.setSubtypes(index, event.target.value)}
                            />
                        </label>
                        {/* Spelled Crexi's way or the run fails — it answers 400 on a word
                            it does not know rather than quietly returning less. */}
                        <div className="ls-note">Crexi's own words, comma separated — e.g. QSR/Fast Food, Day Care/Nursery</div>

                        <div className="ls-limits">
                            {[
                                ['asking_price_min', 'Price from'],
                                ['asking_price_max', 'Price to'],
                                ['sqft_min', 'Building size from'],
                            ].map(([field, label]) => (
                                <label key={field} className="ls-field">
                                    <span className="ls-field__label">{label}</span>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        className="ls-input ls-input--small ls-mono"
                                        placeholder="No limit"
                                        value={search[field] === null || search[field] === undefined ? '' : search[field]}
                                        onChange={(event) => this.setSearch(index, { [field]: event.target.value })}
                                    />
                                </label>
                            ))}
                        </div>
                        {/* A price floor turns off Crexi's unpriced listings, which
                            otherwise sail straight past it. */}
                        {(search.asking_price_min || search.asking_price_max) && (
                            <div className="ls-note">Listings with no asking price are left out</div>
                        )}
                    </div>
                ))}
                </div>

                <button type="button" className="ls-addstate" onClick={() => this.addSearch()}>Add search</button>

                {this.errorFor('searches') && <div className="ls-error">{this.errorFor('searches')}</div>}
            </div>
        );
    }

    render() {
        const { draft, saving, saved } = this.state;
        const { source } = this.props;
        const ceiling = (Number(draft.max_pages) || 1) * LISTINGS_PER_PAGE;
        const dirty = this.dirty();
        const headline = this.errorHeadline();
        const zone = zoneAbbreviation(draft.timezone);

        return (
            <div className="ls-modal">
                <div className="ls-modal__scrim" onClick={this.props.onClose} />

                <div className="ls-modal__dialog" role="dialog" aria-modal="true" aria-label={source.label + ' settings'}>
                    <div className="ls-modal__head">
                        <span className="ls-modal__title">{source.label} settings</span>
                        <button type="button" className="ls-modal__close" aria-label="Close" onClick={this.props.onClose}>
                            ×
                        </button>
                    </div>

                    <div className="ls-modal__body">
                        {headline && (
                            <div className="ls-modal__alert">
                                <span className="ls-alert__mark">!</span>
                                {headline}
                            </div>
                        )}

                        <div className="ls-switchrow">
                            <button
                                type="button"
                                role="switch"
                                aria-checked={Boolean(draft.enabled)}
                                className={'ls-switch' + (draft.enabled ? ' is-on' : '')}
                                onClick={() => this.set('enabled', !draft.enabled)}
                            >
                                <span className="ls-switch__knob" />
                            </button>
                            <div>
                                <div className="ls-switchrow__title">Nightly run</div>
                                <div className="ls-switchrow__help">
                                    {draft.enabled ? 'Runs unattended' : 'Manual runs only'}
                                </div>
                            </div>
                        </div>

                        <div className="ls-modal__columns">
                            <div>
                                <div className="ls-group">
                                    <div className="ls-group__head">
                                        <span className="ls-group__title">Schedule</span>
                                    </div>
                                    <div className="ls-schedule">
                                        <label className="ls-field">
                                            <span className="ls-field__label">Start at</span>
                                            <input
                                                type="text"
                                                className={'ls-input ls-mono' + (this.errorFor('run_at') ? ' is-invalid' : '')}
                                                value={draft.run_at || ''}
                                                onChange={(event) => this.set('run_at', event.target.value)}
                                            />
                                        </label>
                                        {this.renderZonePicker()}
                                    </div>
                                    {/* Without this, "03:00" is read as the viewer's own clock and
                                        the run appears to fire at the wrong time. */}
                                    <div className="ls-note">
                                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor"
                                            strokeWidth="1.5" aria-hidden="true">
                                            <circle cx="6" cy="6" r="4.4" />
                                            <path d="M6 3.6 V6.2 L7.8 7.4" />
                                        </svg>
                                        {draft.run_at} {zone} · {draft.timezone}
                                    </div>
                                    {this.errorFor('run_at') && <div className="ls-error">{this.errorFor('run_at')}</div>}
                                </div>

                                <div className="ls-group">
                                    <div className="ls-group__head">
                                        <span className="ls-group__title">Depth</span>
                                    </div>
                                    <label className="ls-field ls-field--spaced">
                                        <span className="ls-field__label">Page ceiling</span>
                                        <div className="ls-inline">
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                className={'ls-input ls-input--narrow ls-mono' +
                                                    (this.errorFor('max_pages') ? ' is-invalid' : '')}
                                                value={draft.max_pages}
                                                onChange={(event) => this.set('max_pages', event.target.value)}
                                            />
                                            <span className="ls-inline__hint">
                                                pages × 50 = ≤ {formatNumber(ceiling)} listings
                                            </span>
                                        </div>
                                    </label>
                                    {this.errorFor('max_pages') && <div className="ls-error">{this.errorFor('max_pages')}</div>}

                                    <label className="ls-field ls-field--spaced">
                                        <span className="ls-field__label">Cut-off</span>
                                        <div className="ls-inline">
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                className={'ls-input ls-input--narrow ls-mono' +
                                                    (this.errorFor('cutoff_days') ? ' is-invalid' : '')}
                                                value={draft.cutoff_days}
                                                onChange={(event) => this.set('cutoff_days', event.target.value)}
                                            />
                                            <span className="ls-inline__hint">
                                                days back · listed or changed
                                            </span>
                                        </div>
                                    </label>
                                    {this.errorFor('cutoff_days') && <div className="ls-error">{this.errorFor('cutoff_days')}</div>}
                                </div>

                            </div>

                            <div>
                                {this.renderStates()}
                            </div>
                        </div>

                        {/* Full width rather than a third column: the cards are
                            tall and the frame above them is short, so side by
                            side leaves half the dialog empty. */}
                        {this.renderSearches()}
                    </div>

                    <div className={'ls-modal__foot' + (dirty ? ' is-dirty' : '')}>
                        <span className="ls-modal__hint">
                            {saved && !dirty ? 'Saved' : dirty ? 'Unsaved changes' : ''}
                        </span>
                        <button type="button" className="ls-discard" onClick={this.discard}>Discard</button>
                        <button
                            type="button"
                            className="ls-save"
                            disabled={!dirty || saving}
                            onClick={this.save}
                        >
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}
